import { useContext, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { privateRouteLogger } from "../utils/logger";

// Simple loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <svg className="animate-spin h-8 w-8 text-customIndigo mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p className="text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

export default function PrivateRoute({ children }) {
  const { status, testUserValidity } = useContext(AuthContext);
  const location = useLocation();

  privateRouteLogger.log('🛡️ PrivateRoute check:', {
    pathname: location.pathname,
    status: status,
    isAuthenticated: status === "signed_in"
  });

  // Check user validity on route changes, but only if already authenticated
  // This prevents disabled users from continuing to use the app
  useEffect(() => {
    if (status === "signed_in") {
      privateRouteLogger.log('🔍 Checking user validity for route:', location.pathname);
      // Use a small delay to avoid checking on every render
      const timeoutId = setTimeout(() => {
        testUserValidity();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, status, testUserValidity]);

  // Still loading initial Firebase state?
  if (status === "loading") {
    privateRouteLogger.log('⏳ PrivateRoute: Still loading, showing spinner...');
    return <LoadingSpinner />;
  }

  // If backend said "invalid" (e.g. disabled) or we're signed out, go to login
  if (status === "signed_out" || status === "invalid") {
    privateRouteLogger.log('🚫 PrivateRoute: User not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Otherwise render the protected page
  privateRouteLogger.log('✅ PrivateRoute: User authenticated, rendering protected content');
  return children;
}
